import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import iconSearch from '/img/icon-search.svg';
import iconGear from '/img/icon-gear.svg';
import iconExit from '/img/icon-exit.svg';
import logoDark from '/img/logo-tube-archivist-dark.png';
import Routes from '../configuration/routes/RouteList';
import NavigationItem from './NavigationItem';
import logOut from '../api/actions/logOut';
import useIsAdmin from '../functions/useIsAdmin';

const Navigation = () => {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    setIsMobileMenuOpen(false);
    await logOut();
    navigate(Routes.Login);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (searchQuery.trim()) {
      navigate(`${Routes.Search}?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate(Routes.Search);
    }
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header className="modern-header">
      <div className="modern-header-container">
        <div className="modern-header-top-row">
          <Link to={Routes.Home} className="modern-logo-link" onClick={closeMobileMenu}>
            <img src={logoDark} alt="Tube Archivist" className="modern-logo-img" />
            <span className="modern-logo-title">Tube Archivist</span>
          </Link>

          <nav className="modern-nav desktop-nav">
            <div className="modern-nav-links">
              <NavigationItem label="Home" navigateTo={Routes.Home} />
              <NavigationItem label="Channels" navigateTo={Routes.Channels} />
              <NavigationItem label="Playlists" navigateTo={Routes.Playlists} />
              {isAdmin && <NavigationItem label="Downloads" navigateTo={Routes.Downloads} />}
            </div>

            <div className="modern-nav-actions">
              <Link to={Routes.SettingsDashboard} className="nav-action-btn" title="Settings">
                <img src={iconGear} alt="Settings" />
              </Link>
              <button
                type="button"
                className="nav-action-btn alert-hover"
                title="Logout"
                onClick={handleLogout}
              >
                <img src={iconExit} alt="Logout" />
              </button>
            </div>
          </nav>

          <div className="mobile-header-actions">
            <Link
              to={Routes.SettingsDashboard}
              className="nav-action-btn"
              title="Settings"
              onClick={closeMobileMenu}
            >
              <img src={iconGear} alt="Settings" />
            </Link>
            <button
              type="button"
              className="mobile-menu-toggle"
              aria-label={isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>
          </div>
        </div>

        <form onSubmit={handleSearchSubmit} className="header-search-form">
          <div className="header-search-wrapper">
            <input
              type="text"
              placeholder="Search videos, channels, playlists..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="header-search-input"
            />
            <button type="submit" className="header-search-button" title="Search">
              <img src={iconSearch} alt="search" />
            </button>
          </div>
        </form>

        {isMobileMenuOpen && (
          <nav className="mobile-nav-drawer">
            <div className="mobile-nav-links">
              <NavigationItem label="Home" navigateTo={Routes.Home} onClick={closeMobileMenu} />
              <NavigationItem
                label="Channels"
                navigateTo={Routes.Channels}
                onClick={closeMobileMenu}
              />
              <NavigationItem
                label="Playlists"
                navigateTo={Routes.Playlists}
                onClick={closeMobileMenu}
              />
              {isAdmin && (
                <NavigationItem
                  label="Downloads"
                  navigateTo={Routes.Downloads}
                  onClick={closeMobileMenu}
                />
              )}
            </div>
            <div className="mobile-nav-footer">
              <Link
                to={Routes.SettingsDashboard}
                className="mobile-footer-link"
                onClick={closeMobileMenu}
              >
                <img src={iconGear} alt="Settings" />
                <span>Settings</span>
              </Link>
              <button
                type="button"
                className="mobile-footer-link alert-hover"
                onClick={handleLogout}
              >
                <img src={iconExit} alt="Logout" />
                <span>Logout</span>
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Navigation;
